import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Phone, Mail, MapPin, X, Star, ChevronDown, ChevronRight, ChevronLeft, Check, Dumbbell, Target, Heart, Zap, Users, Clock, Award, ArrowRight, Play, Sparkles, Shield, TrendingUp, Activity, CheckCircle2, Calendar, ExternalLink, HelpCircle, MessageCircle, Home, Flame, Mountain, Scan, Apple, CircleDot, ParkingCircle, Globe, Monitor, Video, Instagram, Facebook, Linkedin, Gift, LogIn, Trophy, BarChart3, Link2, Share2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';
import { SEOHead } from '../hooks/usePageSEO';
import { LOGO_URL, STUDIO_IMAGE, LAURENT_IMAGE, TARAH_IMAGE, BOOKING_URL, EMS_SITE_URL, WHATSAPP_URL, SOCIAL_LINKS, IMAGES } from '../data/constants';
import { services } from '../data/services';
import { blogArticles } from '../data/blogData';
import { pricingCategories } from '../data/pricingData';
import { testimonials } from '../data/testimonials';
import { faqItems } from '../data/faqData';
import { AnimatedSection, BackNav, useIntersectionObserver, SectionBadge, AnimatedBadge, ParticlesBackground } from '../components/shared';
import { ServiceCard, ServiceCardWhite } from '../components/ServiceCards';
import { TestimonialsCarousel } from '../components/TestimonialsCarousel';
import { HomeContactSection } from '../components/HomeContactSection';
import { BlogArticleModal } from '../components/BlogArticleModal';

const TermsPage = () => {
  const { language } = useLanguage();
  
  return (
    <main className="pt-32" data-testid="terms-page">
      <SEOHead
        title="Conditions Generales | Body-Up Personal Training Nyon"
        description="Conditions generales de Body-Up Personal Training, Route des Tattes d'Oie 95, 1260 Nyon, Suisse."
        path="/terms"
        noindex={true}
      />
      <section className="py-20 bg-gradient-to-b from-black to-gray-900 min-h-screen">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <BackNav />
          <AnimatedSection>
            <div className="text-center mb-12">
              <h1 className="text-4xl md:text-5xl font-bold text-white">
                {language === 'fr' ? 'Conditions Générales' : 'Terms & Conditions'}
              </h1>
              <p className="text-gray-400 mt-4">
                Body-Up Personal Training
              </p>
              <p className="text-gray-500 text-sm mt-2">
                {language === 'fr' ? 'Dernière mise à jour : 10/12/2025' : 'Last updated: 10/12/2025'}
              </p>
            </div>
          </AnimatedSection>
          
          <AnimatedSection delay={100}>
            <div className="prose prose-invert max-w-none space-y-6">
              {/* Section 1 */}
              <div className="bg-white/5 rounded-2xl p-8 border border-white/10">
                <h2 className="text-2xl font-bold text-white mb-4">
                  {language === 'fr' ? "1. Champ d'application" : '1. Scope of Application'}
                </h2>
                <p className="text-gray-300">
                  {language === 'fr' 
                    ? <>Les présentes conditions générales (ci-après « CGV ») s'appliquent à toutes les prestations proposées par <strong>Body-Up Personal Training</strong> situé Route des Tattes d'Oie 95, 1260 Nyon, Suisse (ci-après « Body-Up »), notamment les séances de coaching personnel, les forfaits d'entraînement, l'EMS Training, le scanner 3D Styku, et tous les services associés.</>
                    : <>These general terms and conditions (hereinafter "GTC") apply to all services offered by <strong>Body-Up Personal Training</strong> located at Route des Tattes d'Oie 95, 1260 Nyon, Switzerland (hereinafter "Body-Up"), including personal coaching sessions, training packages, EMS Training, 3D Styku scanner, and all associated services.</>
                  }
                </p>
                <p className="text-gray-300 mt-4">
                  {language === 'fr' 
                    ? "En réservant une séance ou en achetant un forfait, vous acceptez sans réserve les présentes CGV."
                    : "By booking a session or purchasing a package, you accept these GTC without reservation."
                  }
                </p>
              </div>

              {/* Section 2 */}
              <div className="bg-white/5 rounded-2xl p-8 border border-white/10">
                <h2 className="text-2xl font-bold text-white mb-4">
                  {language === 'fr' ? '2. Inscription et réservation' : '2. Registration and Booking'}
                </h2>
                <ul className="list-disc list-inside text-gray-300 space-y-3">
                  <li>
                    <strong>{language === 'fr' ? 'Âge minimum' : 'Minimum age'}</strong> : {language === 'fr' ? "Les séances sont accessibles aux personnes de 16 ans et plus. Les mineurs doivent obtenir l'autorisation parentale." : "Sessions are accessible to people aged 16 and over. Minors must obtain parental authorization."}
                  </li>
                  <li>
                    <strong>{language === 'fr' ? 'Questionnaire de santé' : 'Health questionnaire'}</strong> : {language === 'fr' ? "Avant toute première séance, un questionnaire de santé doit être rempli. Certaines contre-indications (problèmes cardiaques, grossesse, etc.) peuvent limiter certains exercices ou technologies." : "Before any first session, a health questionnaire must be completed. Certain contraindications (heart problems, pregnancy, etc.) may limit certain exercises or technologies."}
                  </li>
                  <li>
                    <strong>{language === 'fr' ? 'Réservation' : 'Booking'}</strong> : {language === 'fr' ? "Les réservations se font via notre système de réservation en ligne, par téléphone ou par e-mail. Toute réservation est confirmée par un e-mail ou un SMS." : "Bookings are made via our online booking system, by phone or by email. All bookings are confirmed by email or SMS."}
                  </li>
                </ul>
              </div>

              {/* Section 3 */}
              <div className="bg-white/5 rounded-2xl p-8 border border-white/10">
                <h2 className="text-2xl font-bold text-white mb-4">
                  {language === 'fr' ? '3. Tarifs et paiement' : '3. Pricing and Payment'}
                </h2>
                <ul className="list-disc list-inside text-gray-300 space-y-3">
                  <li>
                    <strong>{language === 'fr' ? 'Prix' : 'Prices'}</strong> : {language === 'fr' ? "Les tarifs en vigueur sont affichés sur notre site internet body-up.ch et dans notre studio. Body-Up se réserve le droit de modifier ses tarifs à tout moment." : "Current prices are displayed on our website body-up.ch and in our studio. Body-Up reserves the right to modify its prices at any time."}
                  </li>
                  <li>
                    <strong>{language === 'fr' ? 'Modes de paiement' : 'Payment methods'}</strong> : {language === 'fr' ? "Les paiements peuvent être effectués par carte de crédit, virement bancaire, TWINT ou en espèces. Le paiement est requis avant la prestation ou à l'achat du forfait." : "Payments can be made by credit card, bank transfer, TWINT or cash. Payment is required before the service or at the time of package purchase."}
                  </li>
                  <li>
                    <strong>{language === 'fr' ? 'Forfaits' : 'Packages'}</strong> : {language === 'fr' ? "Les forfaits sont valables 6 mois à compter de la date d'achat, sauf mention contraire. Les forfaits ne sont ni remboursables, ni échangeables, ni cessibles, sauf disposition contraire de la loi." : "Packages are valid for 6 months from the date of purchase, unless otherwise stated. Packages are non-refundable, non-exchangeable and non-transferable, unless otherwise provided by law."}
                  </li>
                </ul>
              </div>

              {/* Section 4 */}
              <div className="bg-white/5 rounded-2xl p-8 border border-white/10">
                <h2 className="text-2xl font-bold text-white mb-4">
                  {language === 'fr' ? '4. Annulation et modification' : '4. Cancellation and Modification'}
                </h2>
                <ul className="list-disc list-inside text-gray-300 space-y-3">
                  <li>
                    <strong>{language === 'fr' ? 'Par le client' : 'By the client'}</strong> : {language === 'fr' ? "Toute annulation ou modification doit se faire au moins 24 heures avant l'heure prévue. En cas d'annulation tardive ou d'absence, la séance est considérée comme consommée et facturée au tarif normal." : "Any cancellation or modification must be made at least 24 hours before the scheduled time. In case of late cancellation or absence, the session is considered consumed and charged at the normal rate."}
                  </li>
                  <li>
                    <strong>{language === 'fr' ? 'Par Body-Up' : 'By Body-Up'}</strong> : {language === 'fr' ? "Body-Up se réserve le droit d'annuler ou de déplacer une séance pour des raisons organisationnelles ou de force majeure. Une séance de remplacement sera proposée sans frais supplémentaires." : "Body-Up reserves the right to cancel or reschedule a session for organizational reasons or force majeure. A replacement session will be offered at no additional cost."}
                  </li>
                </ul>
              </div>

              {/* Section 5 */}
              <div className="bg-white/5 rounded-2xl p-8 border border-white/10">
                <h2 className="text-2xl font-bold text-white mb-4">
                  {language === 'fr' ? "5. Règles d'utilisation du studio" : '5. Studio Usage Rules'}
                </h2>
                <ul className="list-disc list-inside text-gray-300 space-y-3">
                  <li>
                    <strong>{language === 'fr' ? 'Hygiène et tenue' : 'Hygiene and attire'}</strong> : {language === 'fr' ? "Une tenue de sport adaptée et propre est requise pour chaque séance." : "Appropriate and clean sportswear is required for each session."}
                  </li>
                  <li>
                    <strong>{language === 'fr' ? 'Respect du matériel' : 'Equipment care'}</strong> : {language === 'fr' ? "Le client s'engage à utiliser le matériel de manière appropriée et à signaler tout dysfonctionnement." : "The client agrees to use the equipment appropriately and report any malfunction."}
                  </li>
                  <li>
                    <strong>{language === 'fr' ? 'Ponctualité' : 'Punctuality'}</strong> : {language === 'fr' ? "Merci d'arriver 5 minutes avant votre séance. Tout retard impactera la durée de votre séance sans possibilité de prolongation." : "Please arrive 5 minutes before your session. Any delay will impact the duration of your session without possibility of extension."}
                  </li>
                  <li>
                    <strong>{language === 'fr' ? 'Comportement' : 'Conduct'}</strong> : {language === 'fr' ? "Tout comportement inapproprié peut entraîner l'exclusion immédiate sans remboursement." : "Any inappropriate behavior may result in immediate exclusion without refund."}
                  </li>
                </ul>
              </div>

              {/* Section 6 */}
              <div className="bg-white/5 rounded-2xl p-8 border border-white/10">
                <h2 className="text-2xl font-bold text-white mb-4">
                  {language === 'fr' ? '6. Responsabilité et assurance' : '6. Liability and Insurance'}
                </h2>
                <ul className="list-disc list-inside text-gray-300 space-y-3">
                  <li>
                    <strong>{language === 'fr' ? 'État de santé' : 'Health status'}</strong> : {language === 'fr' ? "Le client déclare être en bonne santé et apte à la pratique sportive. Il lui est conseillé de consulter un médecin avant de commencer un programme d'entraînement." : "The client declares to be in good health and fit for sports practice. They are advised to consult a doctor before starting a training program."}
                  </li>
                  <li>
                    <strong>{language === 'fr' ? 'Accidents' : 'Accidents'}</strong> : {language === 'fr' ? "Body-Up dispose d'une assurance responsabilité civile professionnelle. Body-Up décline toute responsabilité en cas de blessure résultant d'une information médicale non divulguée ou du non-respect des consignes de sécurité." : "Body-Up has professional liability insurance. Body-Up disclaims any liability in case of injury resulting from undisclosed medical information or non-compliance with safety instructions."}
                  </li>
                  <li>
                    <strong>{language === 'fr' ? 'Effets personnels' : 'Personal belongings'}</strong> : {language === 'fr' ? "Body-Up ne peut être tenu responsable de la perte ou du vol d'effets personnels dans le studio." : "Body-Up cannot be held responsible for the loss or theft of personal belongings in the studio."}
                  </li>
                </ul>
              </div>

              {/* Section 7 */}
              <div className="bg-white/5 rounded-2xl p-8 border border-white/10">
                <h2 className="text-2xl font-bold text-white mb-4">
                  {language === 'fr' ? '7. Propriété intellectuelle' : '7. Intellectual Property'}
                </h2>
                <p className="text-gray-300">
                  {language === 'fr' 
                    ? "Tous les contenus présents sur le site body-up.ch (textes, images, logos, vidéos) sont protégés par le droit d'auteur. Toute reproduction, représentation ou utilisation, totale ou partielle, est interdite sans autorisation préalable écrite de Body-Up."
                    : "All content on the body-up.ch website (texts, images, logos, videos) is protected by copyright. Any reproduction, representation or use, in whole or in part, is prohibited without prior written authorization from Body-Up."
                  }
                </p>
              </div>

              {/* Section 8 */}
              <div className="bg-white/5 rounded-2xl p-8 border border-white/10">
                <h2 className="text-2xl font-bold text-white mb-4">
                  {language === 'fr' ? '8. Protection des données' : '8. Data Protection'}
                </h2>
                <p className="text-gray-300">
                  {language === 'fr' 
                    ? <>Les données personnelles sont traitées conformément à notre <Link to="/privacy" className="text-primary hover:underline">Politique de Confidentialité</Link> et à la législation suisse (LPD).</>
                    : <>Personal data is processed in accordance with our <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link> and Swiss legislation (DPA).</>
                  }
                </p>
              </div>

              {/* Section 9 */}
              <div className="bg-white/5 rounded-2xl p-8 border border-white/10">
                <h2 className="text-2xl font-bold text-white mb-4">
                  {language === 'fr' ? '9. Modifications des CGV' : '9. Modifications to GTC'}
                </h2>
                <p className="text-gray-300">
                  {language === 'fr' 
                    ? "Body-Up se réserve le droit de modifier les présentes CGV à tout moment. La version en vigueur est celle publiée sur body-up.ch."
                    : "Body-Up reserves the right to modify these GTC at any time. The current version is the one published on body-up.ch."
                  }
                </p>
              </div>

              {/* Section 10 */}
              <div className="bg-white/5 rounded-2xl p-8 border border-white/10">
                <h2 className="text-2xl font-bold text-white mb-4">
                  {language === 'fr' ? '10. Droit applicable et juridiction' : '10. Applicable Law and Jurisdiction'}
                </h2>
                <p className="text-gray-300">
                  {language === 'fr' 
                    ? <>Les présentes CGV sont soumises au droit suisse. En cas de litige, les tribunaux de <strong>Nyon (Suisse)</strong> seront seuls compétents.</>
                    : <>These GTC are governed by Swiss law. In case of dispute, the courts of <strong>Nyon (Switzerland)</strong> shall have exclusive jurisdiction.</>
                  }
                </p>
              </div>

              {/* Contact */}
              <div className="bg-gradient-to-br from-primary/20 to-primary/5 rounded-2xl p-8 border border-primary/30 text-center">
                <h3 className="text-xl font-bold text-white mb-4">
                  {language === 'fr' ? 'Des questions ?' : 'Questions?'}
                </h3>
                <p className="text-gray-300 mb-4">
                  {language === 'fr' 
                    ? "Pour toute question concernant ces conditions, contactez-nous :"
                    : "For any questions regarding these terms, contact us:"
                  }
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a href="mailto:info@body-up.ch" className="inline-flex items-center gap-2 text-primary hover:underline">
                    <Mail className="w-4 h-4" />
                    info@body-up.ch
                  </a>
                  <a href="tel:+41786044423" className="inline-flex items-center gap-2 text-primary hover:underline">
                    <Phone className="w-4 h-4" />
                    +41 78 604 44 23
                  </a>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </main>
  );
};

// Horaires Page - Ultra "Wahou" immersive opening hours

export default TermsPage;
